import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import LanguageSwitcher from './LanguageSwitcher';
import FloatingWhatsApp from './FloatingWhatsApp';
import { localizedPath, stripLanguagePrefix } from '../i18n';

// Must match the logo preload in public/index.html.
export const LOGO_SRC = '/images/ullishtja-logo-140.webp';
export const LOGO_SRCSET = '/images/ullishtja-logo-140.webp 140w, /images/ullishtja-logo-254.webp 254w';

export const buildLangPath = localizedPath;

export function buildHomeHash(hash, lang) {
  return { pathname: localizedPath('/', lang), hash };
}

function SiteNav({ t, currentLanguage, onLanguageChange }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const isMenuPage = stripLanguagePrefix(location.pathname) === '/menu';

  const menuPath = buildLangPath('/menu', currentLanguage);

  const closeMobile = useCallback(() => {
    setMobileOpen(false);
    document.body.classList.remove('scroll-locked');
  }, []);

  const openMobile = useCallback(() => {
    setMobileOpen(true);
    document.body.classList.add('scroll-locked');
  }, []);

  useEffect(() => {
    closeMobile();
  }, [location.pathname, closeMobile]);

  useEffect(() => {
    return () => document.body.classList.remove('scroll-locked');
  }, []);

  const navLinks = [
    { to: buildHomeHash('#home', currentLanguage), label: t.nav.home },
    { to: buildHomeHash('#about', currentLanguage), label: t.nav.about },
    { to: menuPath, label: t.nav.menu, active: isMenuPage },
    { to: buildHomeHash('#contact', currentLanguage), label: t.nav.contact },
  ];

  const renderLink = (link, className, onNavigate) => {
    const classes = `${className}${link.active ? ` ${className}--active` : ''}`;
    return (
      <Link key={link.label} to={link.to} className={classes} onClick={onNavigate}>
        {link.label}
      </Link>
    );
  };

  return (
    <>
      <nav className="navbar" aria-label="Main navigation">
        <div className="nav-container">
          <Link to={localizedPath('/', currentLanguage)} className="logo-home-link logo-container" onClick={closeMobile}>
            <img
              src={LOGO_SRC}
              srcSet={LOGO_SRCSET}
              sizes="70px"
              alt="Ullishtja Agroturizem"
              className="logo"
              width="70"
              height="56"
              loading="eager"
            />
          </Link>

          <div className="nav-menu">
            {navLinks.map((link) => renderLink(link, 'nav-link'))}
          </div>

          <LanguageSwitcher
            variant="navbar"
            currentLanguage={currentLanguage}
            onLanguageChange={onLanguageChange}
          />

          <button
            type="button"
            className="hamburger-btn"
            aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={mobileOpen}
            onClick={() => (mobileOpen ? closeMobile() : openMobile())}
          >
            <span className={`hamburger-line ${mobileOpen ? 'active' : ''}`} />
            <span className={`hamburger-line ${mobileOpen ? 'active' : ''}`} />
            <span className={`hamburger-line ${mobileOpen ? 'active' : ''}`} />
          </button>
        </div>
      </nav>

      {mobileOpen && (
        <button
          type="button"
          className="mobile-nav-backdrop"
          aria-label="Close menu"
          onClick={closeMobile}
        />
      )}

      <div className="mobile-nav-shell">
        <div
          className={`mobile-nav ${mobileOpen ? 'active' : ''}`}
          aria-hidden={!mobileOpen}
          {...(mobileOpen ? {} : { inert: '' })}
        >
          <div className="mobile-nav-header">
            <Link to={localizedPath('/', currentLanguage)} onClick={closeMobile}>
              <img src={LOGO_SRC} srcSet={LOGO_SRCSET} sizes="64px" alt="Ullishtja Agroturizem" className="mobile-logo" width="254" height="199" />
            </Link>
            <button type="button" className="mobile-close-btn" onClick={closeMobile} aria-label="Close">
              ×
            </button>
          </div>

          <nav className="mobile-nav-menu">
            {navLinks.map((link) => renderLink(link, 'mobile-nav-link', closeMobile))}
          </nav>

          <LanguageSwitcher
            variant="drawer"
            currentLanguage={currentLanguage}
            onLanguageChange={onLanguageChange}
          />
        </div>
      </div>

      <FloatingWhatsApp currentLanguage={currentLanguage} t={t} />
    </>
  );
}

export default SiteNav;
